import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useForwardAccumulatorSelector } from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { downloadCSV } from '@/App/tiles/TargetAccumulator/Schedule/ScheduleHeader/utils.ts';
import { isEmpty } from '@/components/sg-design-system/utils/index.ts';
import type { AccumulatorScheduleLine } from '@/store/forwardAccumulator/state.ts';
import { calendarProperties, type SchedulerCalendarProperty } from '@/store/forwardAccumulator/utils.ts';

const ExportSchedule = () => {
  const { formatMessage } = useIntl();
  const schedule = useForwardAccumulatorSelector((forwardAccumulator) => forwardAccumulator?.properties.schedule);

  const scheduleColumns = calendarProperties.filter((props) => props !== 'deleteIcon');

  const headerExportElements = scheduleColumns.map((calendarItem) => formatMessage({ id: headerExport(calendarItem) }));

  const exportSchedule: React.MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      event.stopPropagation();
      const usedScheduleValues = mapScheduleValues(schedule, scheduleColumns);

      if (isNotDefined(usedScheduleValues) && isEmpty(usedScheduleValues)) {
        console.warn('No schedule data to export');
        return;
      }

      const getScheduleList = (scheduleElement?: Record<string, string | number | null>) => {
        if (isDefined(scheduleElement)) {
          return scheduleColumns.map((scheduleElementItem) => scheduleElement[scheduleElementItem]).join(';');
        }
      };

      const output = ['sep=;', headerExportElements.join(';'), ...usedScheduleValues.map(getScheduleList)].join('\r\n');

      downloadCSV(output);
    },
    [scheduleColumns, headerExportElements, schedule],
  );

  return (
    <button type="button" className="btn btn-sm btn-link btn-text-icon me-1" onClick={exportSchedule}>
      <FormattedMessage id="accumulator.scheduler.export" />
      <i className="ms-1 icon icon-sm">save_alt</i>
    </button>
  );
};

export default ExportSchedule;

export const mapScheduleValues = (
  schedule: Record<string, AccumulatorScheduleLine | null> | null | undefined,
  calendarProps: SchedulerCalendarProperty[],
) => {
  if (isNotDefined(schedule)) {
    return [];
  }

  return Object.values(schedule)?.map((value) => {
    if (isDefined(value)) {
      return Object.entries(value).reduce(
        (acc, [key, usedValue]) => {
          console.log({ value });

          if (key === 'step2') {
            acc.EKI = value.step2;
          } else if (calendarProps.includes(key as Partial<SchedulerCalendarProperty>)) {
            acc[key] = usedValue;
          }
          return acc;
        },
        {} as Record<string, string | number | null>,
      );
    }
    return {};
  });
};

export function headerExport(property: Exclude<SchedulerCalendarProperty, 'deleteIcon'>) {
  switch (property) {
    case 'amount':
      return 'accumulator.scheduler.calendar.notionalToExport';
    case 'fixingDate':
      return 'accumulator.scheduler.calendar.fixingDate';
    case 'leverageAmount':
      return 'accumulator.scheduler.calendar.leverageAmountToExport';
    case 'paymentDate':
      return 'accumulator.scheduler.calendar.paymentDate';
    case 'strike':
      return 'accumulator.scheduler.calendar.strike';
    case 'step':
      return 'accumulator.scheduler.calendar.step';

    case 'EKI':
      return 'accumulator.scheduler.calendar.EKI';
    default:
      assertUnreachable(property, `Unknow strike type ${property}`);
  }
}
